import { emptyObjectOf } from "@@/utilities/empty";
import { fullName } from "@@/utilities/full-name";
import { repeatIndex } from "@@/utilities/repeat";
import {
    type DeliverySelection,
    type HomeDeliverySelection,
    type PickUpDeliverySelection,
} from "../carts/cart_old";

import { asDate } from "@@/shared/date-time/as-date";
import { type OrderEventId } from "@@/shared/orders/order-events/events/_order-event-id";
import { type PayRequestId } from "@@/shared/pay-requests/pay-request-id";
import { type PayRequestRecipient } from "@@/shared/pay-requests/pay-request-recipient";
import { type PayRequestRecipientId } from "@@/shared/pay-requests/pay-request-recipient-id";
import { addDays } from "date-fns";
import {
    isoAndUnixFactory,
    type IsoAndUnixTimestamp,
} from "../date-time/iso-and-unix";
import { type MINUTES } from "../date-time/time-constants";
import {
    timeRangeByDurationFactory,
    timeRangeFactory,
} from "../date-time/time-range";
import { type DurationInMinutes } from "../date-time/time-spans";
import {
    deliveryMethodType,
    type DeliveryMethod,
    type DeliveryMethodType,
} from "../deliveries/delivery-methods";
import { type DeliveryZone } from "../deliveries/delivery-zone/delivery-zone";
import { type PickUpDelivery } from "../deliveries/pick-ups/pick-up-delivery";
import { type PickUpDeliveryId } from "../deliveries/pick-ups/pick-up-delivery-id";
import { type ShippingId } from "../deliveries/shipping/shipping-id";
import { emailFactory } from "../email";
import { createAppError } from "../errors/application-error";
import { type AccountNumber } from "../financials/accounting/account-number";
import { accountingDataFactory } from "../financials/accounting/accounting-data";
import { type AccountingDataId } from "../financials/accounting/accounting-data-id";
import { type Address } from "../locations/address";
import { metaContentFactory } from "../meta-content/meta-content";
import { type OrderGroupId } from "../order-groups/order-group-id";
import { type Order } from "../orders/order";
import { type OrderId } from "../orders/order-id";
import { percentageToHuman, type PercentageString } from "../percentage";
import { phoneFactory, type PhoneNumber } from "../phone";
import { zeroPrice } from "../prices/price";
import { vatRatesSweden } from "../prices/vat-rate-sweden";
import { type ProductId } from "../products/product-id";
import { type SkuId } from "../products/sku-id";
import { providerFactory, type ProviderSlug } from "../providers/provider";
import { type ProviderId } from "../providers/provider-id";
import { support } from "../support";
import { tagsFactory } from "../tags";
import { ticketVoucherFactory } from "../tickets/ticket";
import { type User } from "../users/user";
import { type UserAddress } from "../users/user-address";
import { type UserAddressId } from "../users/user-address-id";
import { type UserForProvider } from "../users/user-for-provider";
import { type UserId } from "../users/user-id";

const fakeCreationDate = new Date(2020, 2, 2, 10, 0, 0, 0);
const fakeIsoAndUnixCreationDate = isoAndUnixFactory(fakeCreationDate);
const fakeIsoAndUnixDate = isoAndUnixFactory(new Date(2005, 0, 8, 10, 0, 0, 0));
const fakeTimeRange1 = timeRangeFactory(
    fakeIsoAndUnixDate,
    isoAndUnixFactory(addDays(asDate(fakeIsoAndUnixDate), 1)),
);
const fakeTimeRange2 = timeRangeFactory(
    fakeIsoAndUnixDate,
    isoAndUnixFactory(addDays(asDate(fakeIsoAndUnixDate), 4)),
);

const fakeProviderId = "provider_test_id" as ProviderId;
const fakeOrderId1 = "order__FAKE_ORDER_ID_1" as OrderId;
const fakeOrderSlug1 = "123ABC" as Order["slug"];
const fakeOrderId2 = "order__FAKE_ORDER_ID_2" as OrderId;
const fakeOrderSlug2 = "321CBA" as Order["slug"];
const fakeOrderGroupId1 = "order_group__FAKE_ORDER_GROUP_ID_1" as OrderGroupId;
const fakeOrderGroupId2 = "order_group__FAKE_ORDER_GROUP_ID_2" as OrderGroupId;
const fakeOrderEventId1 = "order_event__FAKE_ORDER_EVENT_ID_1" as OrderEventId;
const fakeOrderEventId2 = "order_event__FAKE_ORDER_EVENT_ID_2" as OrderEventId;
const fakeSkuId1 = "sku__FAKE_SKU_ID_1" as SkuId;
const fakeSkuId2 = "sku__FAKE_SKU_ID_2" as SkuId;
const fakePayRequestId1 = "pay_request__FAKE_PAY_REQUEST_ID_1" as PayRequestId;
const fakePayRequestId2 = "pay_request__FAKE_PAY_REQUEST_ID_2" as PayRequestId;
const fakePayRequestRecipientId1 =
    "pay_request_recipient__FAKE_PAY_REQUEST_RECIPIENT_ID_1" as PayRequestRecipientId;
const fakePayRequestRecipientId2 =
    "pay_request_recipient__FAKE_PAY_REQUEST_RECIPIENT_ID_2" as PayRequestRecipientId;

const fakeAddress: Address = {
    _type: "ADDRESS",
    addressRows: ["Silversköldsgatan 1"],
    city: "Lidköping",
    country: "Sweden",
    zipCode: "53158",
    mapUrls: [],
};

const fakePhoneNumber = support.towni.phoneNumber as PhoneNumber;
const fakePhoneVerified = phoneFactory({
    phoneNumber: fakePhoneNumber,
    verified: true,
});
const fakePhoneUnverified = phoneFactory({
    phoneNumber: fakePhoneNumber,
    verified: false,
});
const fakeEmailAddress = support.towni.supportEmail;
const fakeEmailVerified = emailFactory({
    emailAddress: fakeEmailAddress,
    verified: true,
});
const _fakeEmailUnverified = emailFactory({
    emailAddress: fakeEmailAddress,
    verified: false,
});

const fakeUser: User = {
    _id: "user_FAKE_USER_ID" as UserId,
    _createdAt: fakeIsoAndUnixCreationDate,
    _status: "ACTIVE",
    _type: "USER",

    firstName: "Test",
    lastName: "Testsson",

    email: fakeEmailVerified,
    phone: fakePhoneVerified,
    scopes: [],
    preferences: {},
};
const fakeUserForProvider: UserForProvider = {
    _id: "user_FAKE_USER_FOR_PROVIDER_ID" as UserId,
    _type: "USER_FOR_PROVIDER",
    providerId: fakeProviderId,
    emailAddress: fakeEmailAddress,
    phoneNumber: fakePhoneNumber,
    firstName: "Test",
    lastName: "Testsson",
    scopes: [],
};
const fakeUsersForProvider = (count = 10) =>
    repeatIndex(count).map(count => {
        const output: UserForProvider = {
            ...fakeUserForProvider,
            _id: `user_FAKE_USER_FOR_PROVIDER_ID_${count}` as UserId,
            firstName: fakeUserForProvider.firstName + count,
            lastName: fakeUserForProvider.lastName + count,
        };
        return output;
    });

const fakeUserAddress: UserAddress = {
    _id: "user_address_FAKE_ID" as UserAddressId,
    _createdAt: fakeIsoAndUnixCreationDate,
    _status: "ACTIVE",
    _type: "USERADDRESS",
    userId: fakeUser._id,
    address: fakeAddress,
    name: fullName(fakeUser),
    deliveryInstructions: "Med heders i behåll",
    details: "Våning F, Hiss A, Korridor K, Dörr E",
    inputType: "MANUAL",
};

const fakeMetaContent = metaContentFactory({ title: "FAKE_TITLE" });

const fakeDeliveryZoneZipCodes = ["12345", "23456", "34567"];
const fakeDeliveryZoneMeta = metaContentFactory({ title: "FAKE_ZONE" });
const fakeDeliveryZone: DeliveryZone = {
    _id: "delivery-zone_FAKE_ZONE_ID",
    _createdAt: fakeIsoAndUnixCreationDate,
    _status: "ACTIVE",
    _type: "DELIVERY_ZONE",
    meta: fakeDeliveryZoneMeta,
    zipCodes: fakeDeliveryZoneZipCodes,
    shortIdentifier: "FZ",
};
const fakeBackofficeUser: User = {
    _id: "user_FAKE_BO_USER_ID" as UserId,
    _createdAt: fakeIsoAndUnixCreationDate,
    _status: "ACTIVE",
    _type: "USER",
    firstName: "Bo",
    lastName: "Testsson",
    email: fakeEmailVerified,
    phone: fakePhoneVerified,
    scopes: [`${fakeProviderId}:write`],
    preferences: {},
};
const fakeShippingId = "shipping_FAKE_SHIPPING_ID" as ShippingId;
const fakeProductId1 = "product_FAKE_PRODUCT_ID_1" as ProductId;
const fakeProductId2 = "product_FAKE_PRODUCT_ID_2" as ProductId;
const fakeProvider = providerFactory({
    _id: fakeProviderId,
    address: fakeAddress,
    emails: [fakeEmailVerified],
    phones: [fakePhoneUnverified],
    meta: {
        _type: "META_CONTENT",
        _createdAt: isoAndUnixFactory(new Date(2020, 2, 20, 12, 0, 0)),
        title: "Fakey McFakeface",
        description: "Fakey McFakeface is a fake provider",
        images: [],
        tags: tagsFactory(),
        kicker: null,
        subtitle: "It really whips...",
        actionText: null,
        background: undefined,
    },
    slug: "fakey" as ProviderSlug,
    ownerIds: [
        fakeBackofficeUser._id,
        (fakeUserForProvider._id + "_0") as UserId,
        (fakeUserForProvider._id + "_3") as UserId,
    ],
    options: {
        prepTime: 15 as DurationInMinutes,
        defaultOrderTimePadding: {
            startInMinutes: 0 as MINUTES,
            endInMinutes: 0 as MINUTES,
        },
        prepTimeOverride: null,
        businessHourExceptions: [],
        businessHours: {
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
            sun: false,
        },
        paused: null,
        acceptTips: false,
    },
});
const fakeTicket1 = ticketVoucherFactory({
    valid: timeRangeByDurationFactory(
        new Date(),
        {
            value: 30,
            granularity: "HOUR",
        }, //Read from product
    ),
    meta: metaContentFactory({ title: "TEST" }),
    productId: fakeProductId1,
    code: "CODE12",
    status: "ACTIVE",
    providerId: fakeProviderId,
    price: zeroPrice,
    history: [],
    shortLink: "",
    contentBlock: [],
    optionals: [],
});

const fakeDelivery = (
    deliveryMethod: DeliveryMethod,
    selectedDeliveryTime: IsoAndUnixTimestamp,
) => {
    switch (deliveryMethod.type) {
        case deliveryMethodType.PICK_UP: {
            const fakePickupDelivery: PickUpDelivery = {
                _id: "delivery_FAKE_PICKUP_ID" as PickUpDeliveryId,
                _createdAt: fakeIsoAndUnixCreationDate,
                _status: "ACTIVE",
                _type: "PICK_UP_DELIVERY",
                deliveryMethodType: deliveryMethodType.PICK_UP,
                pickUpAddress: fakeProvider.address,
                estimatedDeliveryTime: selectedDeliveryTime,
                requestedDeliveryTime: "ASAP",
                pickUpInstructions: null,
                destination: {
                    _type: "PICK_UP_DESTINATION",
                    recipientType: "USER",
                    recipientId: fakeUser._id,
                    recipientName: fullName(fakeUser),
                    contact: {
                        emails: fakeProvider.emails,
                        phones: fakeProvider.phones,
                    },
                },
                origin: {
                    _type: "PICK_UP_ORIGIN",
                    senderType: "PROVIDER",
                    senderId: fakeProvider._id,
                    senderName: "Sender",
                    contact: {
                        emails: fakeProvider.emails,
                        phones: fakeProvider.phones,
                    },
                },
            };
            return fakePickupDelivery;
        }
        default:
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw createAppError({
                errorCode: "INTERNAL_ERROR",
                message:
                    "Unhandled delivery method type (" +
                    deliveryMethod.type +
                    ")",
                publicMessage:
                    "Problem med leveranstypen, försök igen senare eller testa en annan leveranstyp",
            });
    }
};

const fakeAccountingData = (
    providerId: ProviderId = fakeProviderId,
    productAccountMapping: Record<ProductId, AccountNumber> = emptyObjectOf<
        Record<ProductId, AccountNumber>
    >(),
) =>
    accountingDataFactory({
        _id: "accounting_data__FAKE" as AccountingDataId,
        _createdAt: fakeIsoAndUnixCreationDate,
        productAccountMapping,
        vatAccountMapping: vatRatesSweden.reduce(
            (mapping, vatRate) => {
                mapping[percentageToHuman(vatRate)] = `26${
                    vatRate * 100
                }` as AccountNumber;
                return mapping;
            },
            {} as Record<PercentageString, AccountNumber>,
        ),
        ownerId: providerId,
        accountReceivable: "1910" as AccountNumber,
        fallbackProductAccount: "8888" as AccountNumber,
        fallbackVatAccount: "2600" as AccountNumber,
        accountingDimensions: [],
    });

const fakeDeliverySelection = (
    method: DeliveryMethodType,
    at: Date | undefined,
): DeliverySelection => {
    switch (method) {
        case "PICK_UP": {
            const selection: PickUpDeliverySelection = {
                method,
                price: zeroPrice,
                slot: at
                    ? {
                          _id: "FAKE_TIME_SPOT_ID",
                          _type: "TIME_SPOT",
                          start: isoAndUnixFactory(at),
                      }
                    : undefined,
            };
            return selection;
        }
        case "HOME_DELIVERY": {
            const selection: HomeDeliverySelection = {
                method,
                deliveryInstruction: "Med hedern i behåll",
                destination: fakeUserAddress,
                shipping: fakeShippingId,
                shippingTime: 20 as MINUTES,
                zone: fakeDeliveryZone,
                price: zeroPrice,
                slot: at
                    ? {
                          _id: "FAKE_TIME_SPOT_ID",
                          _type: "TIME_SPOT",
                          start: isoAndUnixFactory(at),
                      }
                    : undefined,
            };
            return selection;
        }
        default:
            throw new Error("Unhandled delivery method type (" + method + ")");
    }
};
const fakePayRequestRecipient1: PayRequestRecipient = {
    _id: fakePayRequestRecipientId1,
    _type: "PAY_REQUEST_RECIPIENT",
    type: "SMS",
    phoneNumber: fakeUser.phone.phoneNumber,
    language: "sv",
    name: fullName(fakeUser),
};
const fakePayRequestRecipient2: PayRequestRecipient = {
    _id: fakePayRequestRecipientId2,
    _type: "PAY_REQUEST_RECIPIENT",
    type: "SMS",
    phoneNumber: fakeUser.phone.phoneNumber,
    language: "sv",
    name: fullName(fakeUser),
};

export {
    fakeAccountingData,
    fakeBackofficeUser,
    fakeDelivery,
    fakeDeliverySelection,
    fakeIsoAndUnixDate,
    fakeMetaContent,
    fakeOrderEventId1,
    fakeOrderEventId2,
    fakeOrderGroupId1,
    fakeOrderGroupId2,
    fakeOrderId1,
    fakeOrderId2,
    fakeOrderSlug1,
    fakeOrderSlug2,
    fakePayRequestId1,
    fakePayRequestId2,
    fakePayRequestRecipient1,
    fakePayRequestRecipient2,
    fakePayRequestRecipientId1,
    fakePayRequestRecipientId2,
    fakeProductId1,
    fakeProductId2,
    fakeProvider,
    fakeSkuId1,
    fakeSkuId2,
    fakeTicket1,
    fakeTimeRange1,
    fakeTimeRange2,
    fakeUser,
    fakeUserForProvider,
    fakeUsersForProvider,
};
